import { CheckCircle2, CircleAlert, Copy, ExternalLink } from 'lucide-react'
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom'
import {
  Badge, Button, Card, CardHeader, ErrorState, LoadingState, PageHeader, toast, useResource,
} from '@retink/web-ui'
import { api } from '../../lib/api'

interface SetupStatus {
  workspaceCount: number
  memberCount: number
  activeAdminCount: number
  microsoft: { id: string; status: string; domains: string[] } | null
  pendingOwner: { email: string; expiresAt: string } | null
  ready: boolean
  nextAction: 'create_workspace' | 'connect_microsoft' | 'complete_owner_setup'
    | 'replace_owner_link' | 'recover_owner' | 'invite_people'
}

interface OwnerSetup { ownerEmail: string; setupUrl: string; expiresAt: string }
type OrgCtx = { orgName: string }

const actionCopy: Record<SetupStatus['nextAction'], { title: string; body: string; section: string; label: string }> = {
  create_workspace: {
    title: 'Create the first workspace', body: 'This organization has nowhere for members to work yet.', section: 'workspaces', label: 'Create workspace',
  },
  connect_microsoft: {
    title: 'Connect Microsoft', body: 'Add the company domain and first administrator. Retink discovers the directory.', section: 'identity', label: 'Connect Microsoft',
  },
  complete_owner_setup: {
    title: 'Owner setup waiting', body: 'The named administrator must open their setup link, approve Microsoft, and sign in.', section: 'identity', label: 'View identity setup',
  },
  replace_owner_link: {
    title: 'Create a replacement owner link', body: 'Microsoft is not active and no usable owner link remains.', section: 'identity', label: 'Replace setup link',
  },
  recover_owner: {
    title: 'Organization needs an administrator', body: 'Microsoft is connected, but no active organization administrator exists.', section: 'people', label: 'Recover administrator',
  },
  invite_people: {
    title: 'Organization ready', body: 'Microsoft and the first administrator are active. Assign people when ready.', section: 'people', label: 'Manage people',
  },
}

export function OrgDashboard() {
  const { orgId } = useParams<{ orgId: string }>()
  const { orgName } = useOutletContext<OrgCtx>()
  const location = useLocation()
  const ownerSetup = (location.state as { ownerSetup?: OwnerSetup } | null)?.ownerSetup
  const { data: status, loading, error, reload } = useResource<SetupStatus>(
    () => api.get(`/api/orgs/${orgId}/setup-status`),
    [orgId],
  )

  if (loading) return <div className="p-8"><LoadingState /></div>
  if (error || !status) return <div className="p-8"><ErrorState message={error ?? 'Could not load setup'} onRetry={reload} /></div>
  const next = actionCopy[status.nextAction]

  async function copySetup() {
    if (!ownerSetup) return
    await navigator.clipboard?.writeText(ownerSetup.setupUrl)
    toast.success('Owner setup link copied')
  }

  return (
    <div className="max-w-4xl p-8">
      <PageHeader title="Overview" description={`Setup and access state for ${orgName}.`} />

      {ownerSetup && (
        <Card className="mb-6 border-primary/40">
          <CardHeader>Owner setup link ready</CardHeader>
          <div className="space-y-3 px-4 pb-4 text-sm">
            <p>Give this one-time link to <strong>{ownerSetup.ownerEmail}</strong>. No platform login required.</p>
            <p className="break-all rounded-md bg-muted p-3 font-mono text-xs">{ownerSetup.setupUrl}</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => void copySetup()}><Copy /> Copy link</Button>
              <Button variant="outline" onClick={() => window.open(ownerSetup.setupUrl, '_blank', 'noopener,noreferrer')}><ExternalLink /> Open</Button>
            </div>
            <p className="text-xs text-muted-foreground">Expires {new Date(ownerSetup.expiresAt).toLocaleString()}.</p>
          </div>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader>Next action</CardHeader>
        <div className="flex flex-col gap-4 px-4 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-medium">{next.title}</div>
            <p className="mt-1 text-sm text-muted-foreground">{next.body}</p>
            {status.pendingOwner && (
              <p className="mt-2 text-xs text-muted-foreground">
                Waiting for {status.pendingOwner.email}; link expires {new Date(status.pendingOwner.expiresAt).toLocaleString()}.
              </p>
            )}
          </div>
          <Link to={`/organizations/${orgId}/${next.section}`} className="shrink-0 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            {next.label}
          </Link>
        </div>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <SetupFact ready={status.workspaceCount > 0} title="Workspace" detail={status.workspaceCount === 1 ? '1 workspace' : `${status.workspaceCount} workspaces`} />
        <SetupFact ready={status.microsoft?.status === 'active'} title="Microsoft" detail={status.microsoft ? `${status.microsoft.domains.join(', ')} · ${status.microsoft.status}` : 'Not connected'} />
        <SetupFact ready={status.activeAdminCount > 0} title="Organization admin" detail={status.activeAdminCount === 1 ? '1 active admin' : `${status.activeAdminCount} active admins`} />
        <SetupFact ready={status.memberCount > 0} title="Workspace people" detail={status.memberCount === 1 ? '1 assigned person' : `${status.memberCount} assigned people`} optional />
      </div>
    </div>
  )
}

function SetupFact({ ready, title, detail, optional = false }: { ready: boolean; title: string; detail: string; optional?: boolean }) {
  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        {ready ? <CheckCircle2 className="mt-0.5 size-5 text-success" /> : <CircleAlert className="mt-0.5 size-5 text-warning" />}
        <div>
          <div className="flex items-center gap-2 font-medium">{title}{optional && <Badge tone="neutral">optional</Badge>}</div>
          <div className="mt-1 text-sm text-muted-foreground">{detail}</div>
        </div>
      </div>
    </Card>
  )
}
